// PaymentMethodModal/AccountVerificationSection.tsx
import React from "react";
import { Eye, EyeOff, Check } from "lucide-react";
import { CustomButton } from "@/components/ui/Buttons";
import toast from "react-hot-toast";

import { AccountVerificationState, FormSectionProps } from "./type";
import { validateAccountNumber } from "@/utils/PaymentMethodModalUtils";

interface AccountVerificationSectionProps extends FormSectionProps {
  accountVerification: AccountVerificationState;
  setAccountVerification: React.Dispatch<
    React.SetStateAction<AccountVerificationState>
  >;
  selectedCountry: string;
}

const AccountVerificationSection: React.FC<AccountVerificationSectionProps> = ({
  formData,
  accountVerification,
  setAccountVerification,
  selectedCountry,
  onFormDataChange,
}) => {
  const handleAccountNumberNext = () => {
    const validation = validateAccountNumber(
      accountVerification.enteredNumber,
      selectedCountry
    );

    if (!validation.isValid) {
      toast.error(validation.message || "Invalid account number");
      return;
    }

    setAccountVerification((prev) => ({ ...prev, step: "verify" }));
  };

  const handleAccountNumberVerify = () => {
    if (
      accountVerification.enteredNumber !== accountVerification.confirmNumber
    ) {
      toast.error("Account numbers do not match");
      setAccountVerification((prev) => ({ ...prev, confirmNumber: "" }));
      return;
    }

    onFormDataChange("accountNumber", accountVerification.enteredNumber, true);
    setAccountVerification((prev) => ({ ...prev, verified: true }));
    toast.success("Account number verified successfully");
  };

  const handleAccountNumberEdit = () => {
    setAccountVerification((prev) => ({
      ...prev,
      step: "enter",
      confirmNumber: "",
      verified: false,
      enteredNumber: formData.details.accountNumber,
    }));
  };

  const toggleShowEnteredNumber = () => {
    setAccountVerification((prev) => ({
      ...prev,
      showEnteredNumber: !prev.showEnteredNumber,
    }));
  };

  const toggleShowConfirmNumber = () => {
    setAccountVerification((prev) => ({
      ...prev,
      showConfirmNumber: !prev.showConfirmNumber,
    }));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        Account Number
        {accountVerification.verified && (
          <span className="ml-2 text-green-600 text-xs inline-flex items-center">
            <Check size={14} className="mr-1" />
            Verified
          </span>
        )}
      </label>

      {accountVerification.step === "enter" ? (
        <div className="space-y-3">
          <div className="relative">
            <input
              type={accountVerification.showEnteredNumber ? "text" : "password"}
              value={accountVerification.enteredNumber}
              onChange={(e) =>
                setAccountVerification((prev) => ({
                  ...prev,
                  enteredNumber: e.target.value,
                }))
              }
              placeholder="Enter your account number"
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#000200]"
              required
            />
            <button
              type="button"
              onClick={toggleShowEnteredNumber}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
            >
              {accountVerification.showEnteredNumber ? (
                <EyeOff size={16} />
              ) : (
                <Eye size={16} />
              )}
            </button>
          </div>
          <CustomButton
            type="button"
            variant="outline"
            color="black"
            onClick={handleAccountNumberNext}
            className="w-full"
            disabled={!accountVerification.enteredNumber.trim()}
          >
            Next
          </CustomButton>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="p-3 bg-gray-50 rounded-md border">
            <p className="text-sm text-gray-600 mb-2">Account Number:</p>
            <p className="font-mono text-sm">
              {accountVerification.verified
                ? `${"*".repeat(
                    accountVerification.enteredNumber.length - 4
                  )}${accountVerification.enteredNumber.slice(-4)}`
                : "*".repeat(accountVerification.enteredNumber.length)}
            </p>
            <button
              type="button"
              onClick={handleAccountNumberEdit}
              className="text-blue-600 text-xs hover:underline mt-1"
            >
              Edit
            </button>
          </div>

          {!accountVerification.verified && (
            <>
              <div className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Confirm Account Number
                </label>
                <div className="relative">
                  <input
                    type={
                      accountVerification.showConfirmNumber
                        ? "text"
                        : "password"
                    }
                    value={accountVerification.confirmNumber}
                    onChange={(e) =>
                      setAccountVerification((prev) => ({
                        ...prev,
                        confirmNumber: e.target.value,
                      }))
                    }
                    placeholder="Re-enter account number to verify"
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#000200]"
                    required
                  />
                  <button
                    type="button"
                    onClick={toggleShowConfirmNumber}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {accountVerification.showConfirmNumber ? (
                      <EyeOff size={16} />
                    ) : (
                      <Eye size={16} />
                    )}
                  </button>
                </div>
              </div>
              <CustomButton
                type="button"
                onClick={handleAccountNumberVerify}
                className="w-full"
                disabled={!accountVerification.confirmNumber.trim()}
              >
                Verify Account Number
              </CustomButton>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AccountVerificationSection;
